import { isInSeason, type SeasonProgress, type SeasonRegion } from '@sea-snag/shared';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The season strip's bar and words, both from the organisation's own season
 * for the year (its configured dates, or the regional default). Taking both
 * from one source keeps them in step (#256), and a season that hasn't started
 * reads as such, e.g. next year's (#265).
 *
 * Weeks are counted in sevens from the season's first day, the last one
 * short, so "week X of Y" and "N weeks left" always add up.
 */
export function seasonStatus(
  progress: SeasonProgress,
  yearRound: boolean
): { pct: number; text: string } {
  const pct = Math.round(progress.elapsedPct);
  if (yearRound) return { pct, text: `${pct}% through the year` };

  switch (progress.state) {
    case 'before':
      return {
        pct: 0,
        text: `Not started yet · starts in ${plural(progress.daysUntilStart, 'day')}`,
      };
    case 'after':
      return { pct: 100, text: 'Season over' };
    case 'in': {
      const totalWeeks = Math.ceil(progress.totalDays / 7);
      const week = Math.min(totalWeeks, Math.floor(progress.daysElapsed / 7) + 1);
      const weeksLeft = totalWeeks - week;
      const left = weeksLeft === 0 ? 'last week' : `${plural(weeksLeft, 'week')} left`;
      return { pct, text: `Week ${week} of ${totalWeeks} · ${pct}% in · ${left}` };
    }
  }
}

/**
 * Whether a day (an ISO date) falls in the organisation's season: its own
 * dates for the year once they've loaded, both ends included, and the regional
 * preset until then. Occupancy's grid shades, filters and counts weeks by this,
 * so it agrees with the strip above it (#256).
 */
export function inSeasonOn(
  isoDate: string,
  region: SeasonRegion,
  season?: { startDate: string; endDate: string }
): boolean {
  if (season) return isoDate >= season.startDate && isoDate <= season.endDate;
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return false;
  // The preset is month/day only; 2000 is any leap year, so 29 Feb resolves.
  return isInSeason(region, new Date(2000, m - 1, d));
}
